import { useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CardSection from '@shared/components/CardSection';
import ArtifactSelector from './ArtifactSelector';
import EcdisTargetList from './EcdisTargetList';
import TransferStatus from './TransferStatus';
import { useCreateTransfer } from '../hooks/useCreateTransfer';

const steps = ['Select Artifact', 'Select Targets', 'Confirm', 'Transfer'];

export default function TransferWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
  const [operationId, setOperationId] = useState<string | null>(null);
  const createTransfer = useCreateTransfer();

  function handleToggleTarget(id: string) {
    setSelectedTargetIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  }

  function handleConfirmAndSend() {
    if (!selectedArtifactId) return;
    createTransfer.mutate(
      { artifactId: selectedArtifactId, targetIds: selectedTargetIds },
      {
        onSuccess: (data) => {
          setOperationId(data.operationId);
          setActiveStep(3);
        },
      },
    );
  }

  function handleReset() {
    setActiveStep(0);
    setSelectedArtifactId(null);
    setSelectedTargetIds([]);
    setOperationId(null);
    createTransfer.reset();
  }

  return (
    <div data-testid="transfer-wizard" className="flex flex-col gap-3">
      <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <>
          <ArtifactSelector
            selectedArtifactId={selectedArtifactId}
            onSelect={setSelectedArtifactId}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!selectedArtifactId}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {activeStep === 1 && (
        <>
          <EcdisTargetList selectedIds={selectedTargetIds} onToggle={handleToggleTarget} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
              disabled={selectedTargetIds.length === 0}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {activeStep === 2 && (
        <>
          <CardSection title="Confirm Transfer">
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Artifact:</strong> {selectedArtifactId}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Targets:</strong> {selectedTargetIds.join(', ')}
            </Typography>

            {createTransfer.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createTransfer.error?.message ?? 'Transfer creation failed'}
              </Alert>
            )}
          </CardSection>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Button onClick={() => setActiveStep(1)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleConfirmAndSend}
              disabled={createTransfer.isPending}
              startIcon={createTransfer.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {createTransfer.isPending ? 'Sending…' : 'Send Transfer'}
            </Button>
          </Box>
        </>
      )}

      {activeStep === 3 && operationId && (
        <>
          <TransferStatus operationId={operationId} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={handleReset}>
              New Transfer
            </Button>
          </Box>
        </>
      )}
    </div>
  );
}
